<template>
    <GuidedSetupChrome ref="chrome" :label="t('onboarding.profileSetup.page.title')" :show-overview="setup.activeStep.value === null" @escape="setup.closeStep">
        <!-- Keep the live region mounted outside the chrome's conditionals so screen transitions update its
             text without recreating it. It carries no accessible name: a name on a live region is announced
             in place of the text that changed.
             -->
        <template #status>
            <p class="sr-only" role="status">{{ screenStatus }}</p>
        </template>

        <template #overview>
            <ContextSummary
                ref="overview"
                :steps="setup.steps.value"
                :context="setup.context.value"
                :completed-count="setup.completedCount.value"
                :readings="setup.readings.value"
                :readings-loading="setup.readingsLoading.value"
                :chat-input="chatInput"
                @open="setup.openStep" />
        </template>

        <template #sections>
            <BaseSectionNav
                :label="t('onboarding.profileSetup.page.sectionsLabel')"
                :items="navItems"
                :model-value="setup.activeStep.value ?? undefined"
                @update:model-value="openStep" />
        </template>

        <template #step>
            <GuidedSetupStepRunner
                v-if="setup.activeStep.value"
                :key="setup.activeStep.value"
                :setup="setup"
                :step-id="setup.activeStep.value"
                :invite-url="inviteUrl"
                :directory-enabled="directoryEnabled"
                @scroll-reset="resetScroll"
                @status-change="stepStatus = $event" />
        </template>
    </GuidedSetupChrome>
</template>

<script setup lang="ts">
import BaseSectionNav from "~vue/components/BaseSectionNav.vue";
import ContextSummary from "~vue/ProfileSetup/ContextSummary.vue";
import GuidedSetupChrome from "~vue/ProfileSetup/GuidedSetupChrome.vue";
import GuidedSetupStepRunner from "~vue/ProfileSetup/GuidedSetupStepRunner.vue";
import { SETUP_SECTION_ICONS } from "~vue/ProfileSetup/setupSections";
import type { ChatInputProps, SetupStep, SetupStepId } from "~vue/ProfileSetup/types";
import type { ProfileSetup } from "~vue/ProfileSetup/useProfileSetup";
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const {
    setup,
    chatInput = null,
    inviteUrl,
    directoryEnabled,
} = defineProps<{
    setup: ProfileSetup;
    inviteUrl: string;
    /** Whether the coworker directory API will serve this user; it 404s without its flags. */
    directoryEnabled: boolean;
    /** Null for hosts without chat configuration, including the dev gallery. */
    chatInput?: ChatInputProps | null;
}>();
const chrome = useTemplateRef<{ focusPage: () => void; resetScroll: () => Promise<void> }>("chrome");
const overview = useTemplateRef<{ focus: () => void }>("overview");
onMounted(() => chrome.value?.focusPage());

/*
 * Closing an ask removes the focused "All asks" button. Focus the overview to preserve the keyboard user's
 * position.
 */
watch(
    () => setup.activeStep.value,
    async (step, previous) => {
        if (step !== null || previous === null) {
            return;
        }
        await nextTick();
        overview.value?.focus();
    },
);

/*
 * Where the open ask has moved to since it opened, reported by its runner. Null until it moves, so opening
 * an ask announces the ask itself.
 */
const stepStatus = ref<string | null>(null);

watch(
    () => setup.activeStep.value,
    () => {
        stepStatus.value = null;
    },
);

/*
 * Steps can be opened in any order, so announce their names rather than ordinal positions. Once an ask has a
 * screen, that screen is the news: a wait and the result that replaces it have to sound different, or the
 * user hears nothing when a pipeline finishes.
 */
const screenStatus = computed(() => {
    const step = setup.activeStep.value;
    if (step === null) {
        return t("onboarding.profileSetup.page.overviewStatus");
    }
    return stepStatus.value ?? t("onboarding.profileSetup.page.stepStatus", { step: stepLabel(step) });
});

/*
 * Readings are absent from page props. Fetch on arrival and when setup becomes complete so newly finished
 * users see them without reloading.
 */
watch(
    () => setup.steps.value.every((step) => step.done),
    (complete) => {
        if (complete) {
            setup.loadReadings();
        }
    },
    { immediate: true },
);

const navItems = computed(() =>
    setup.steps.value.map((step) => {
        const blocker = setup.blockedBy(step.id);
        return {
            id: step.id,
            label: step.label,
            icon: SETUP_SECTION_ICONS[step.id],
            completed: step.done,
            disabled: blocker !== null,
            note: blocker === null ? undefined : t("onboarding.profileSetup.page.completeFirst", { step: stepLabel(blocker) }),
            accessibleLabel: t("onboarding.profileSetup.page.stepAccessibleLabel", { label: step.label, status: statusLabel(step) }),
        };
    }),
);

function stepLabel(id: SetupStepId): string {
    return setup.steps.value.find((step) => step.id === id)?.label ?? "";
}

function openStep(id: string | undefined): void {
    const step = setup.steps.value.find((step) => step.id === id);
    if (step) {
        setup.openStep(step.id);
    }
}

function statusLabel(step: SetupStep): string {
    if (step.done) {
        return t("onboarding.profileSetup.page.status.done");
    }
    const blocker = setup.blockedBy(step.id);
    if (blocker !== null) {
        return t("onboarding.profileSetup.page.status.locked", { step: stepLabel(blocker) });
    }
    return step.id === setup.activeStep.value ? t("onboarding.profileSetup.page.status.inProgress") : t("onboarding.profileSetup.page.status.notStarted");
}

async function resetScroll(): Promise<void> {
    await chrome.value?.resetScroll();
}
</script>
